import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiPhotoGetGallery, apiPhotoDownload } from '../../../actions/photoActions';
import { isEmpty } from '../../../core/helpers';
import Loading from '../../../components/ui/Loading';
import moment from 'moment';
import { CLOUDFRONT_URL } from '../../../core/common';
import { useSelector } from 'react-redux';
import PageNotFound from '../../pagenotfound/PageNotFound';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

function PhotoPage() {
  const { id } = useParams();
  const token = useSelector(state => state.user.token);
  const role = useSelector(state => state.user.user?.role);
  const user = useSelector(state => state.user.user);

  const [loading, setLoading] = useState(true);
  const [album, setAlbum] = useState<any>(null);
  const [notFound, setNotFound] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const loadData = async () => {
    if (id !== undefined) {
      try {
        const response: any = await apiPhotoGetGallery(parseInt(id));
        if (response.status) {
          setAlbum(response.album);
          setLoading(false);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        setNotFound(true);
      }
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePhotoClick = (index: number) => {
    setSelectedIndex(index);
    setIsLightboxOpen(true);
  };

  const handleDownload = async (photoId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await apiPhotoDownload(token, photoId);
      if (response.status && response.link) {
        window.open(response.link, '_blank');
      }
    } catch (error) {
      console.error('Error downloading photo:', error);
    }
  };

  if (notFound) {
    return <PageNotFound />;
  }

  if (loading || album === null) {
    return <Loading />;
  }

  if (role === 'client' && user?.photos_access === 0) {
    return (
      <div className="error-page">
        <div className="error-page-container">
          <h1 className="error-page-title">Oh, shoot</h1>
          <div className="error-page-image-area">
            <div className="error-page-image" />
          </div>
          <span className="error-page-sub-title">You do not have access to this page.</span>
          <div className="error-page-text">
            To access to our photo library, please contact us at{' '}
            <a className="blue" href="mailto:info@stringershub.com">
              info@stringershub.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!token || (role !== 'admin' && user?.photos_access === 0)) {
    return <PageNotFound />;
  }

  return (
    <>
      <div className="video-page-top">
        <Link to={'/photos'} className="video-page-top-back-icon">
          <i className="fa fa-angle-left" />
        </Link>
        <span className="video-page-top-title">Photos</span>
      </div>
      <div className="gallery">
        <div className="gallery-left">
          <div className="photo-page-grid">
            {album.photos.map((photo: any, index: number) => (
              <div key={photo.id} className="photo-page-grid-item">
                <img
                  src={`${CLOUDFRONT_URL}${photo.public_path}`}
                  alt=""
                  className="photo-page-grid-image"
                />
                <div className="photo-page-grid-actions">
                  {user && (parseInt(user.photos_download_access) === 1 || role === 'admin') && (
                    <div
                      className="photo-page-grid-action download"
                      onClick={e => handleDownload(photo.id, e)}
                    >
                      <i className="fa fa-download" />
                    </div>
                  )}
                  <div
                    className="photo-page-grid-action"
                    onClick={e => {
                      e.stopPropagation();
                      handlePhotoClick(index);
                    }}
                  >
                    <i className="fa fa-search-plus" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="gallery-right">
          {album.title && <h1 className="gallery-title">{album.title}</h1>}
          <div className="gallery-elements">
            <div className="gallery-element">
              <span className="gallery-element-label">Uploaded at:</span>
              <span className="gallery-element-value">
                {moment(album.created_at).format('DD/MM/YYYY')}
              </span>
            </div>
            {!isEmpty(album.filming_date) && (
              <div className="gallery-element">
                <span className="gallery-element-label">Filming Date:</span>
                <span className="gallery-element-value">
                  {moment(album.filming_date).format('DD/MM/YYYY')}
                </span>
              </div>
            )}
            {!isEmpty(album.location_text) && (
              <div className="gallery-element">
                <span className="gallery-element-label">Location:</span>
                <span className="gallery-element-value">{album.location_text}</span>
              </div>
            )}
          </div>
          {!isEmpty(album.description) && (
            <div
              className="gallery-description"
              dangerouslySetInnerHTML={{ __html: album.description.replace(/\n/g, '<br />') }}
            />
          )}
        </div>
      </div>
      <Lightbox
        open={isLightboxOpen}
        close={() => setIsLightboxOpen(false)}
        index={selectedIndex}
        slides={album.photos.map((photo: any) => ({
          src: `${CLOUDFRONT_URL}${photo.public_path}`,
        }))}
        carousel={{
          finite: true,
          preload: 2,
        }}
        animation={{
          fade: 300,
          swipe: 500,
        }}
        controller={{
          closeOnBackdropClick: true,
          closeOnPullDown: true,
        }}
      />
    </>
  );
}

export default PhotoPage;
